import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { FullPageSpinner } from '@/components/common/LoadingSpinner';
import { LoginPage } from '@/components/auth/LoginPage';
import { NotAuthorizedPage } from '@/components/auth/NotAuthorizedPage';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * The `/users/me` verification spinner is bounded — if the call hasn't
 * resolved in this window (e.g. MSAL silent-token acquisition hangs because
 * third-party cookies are blocked in incognito, or the backend is unreachable),
 * we stop waiting and route to the recovery page rather than stranding the
 * user on an indeterminate spinner. 8s comfortably covers a healthy call
 * (typically <500ms) while not feeling abandoned on a slow network.
 */
const VERIFICATION_TIMEOUT_MS = 8_000;

export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading, isLoadingRoles, isUnauthorized, meCheckFailed } = useAuth();
  const [verificationTimedOut, setVerificationTimedOut] = useState(false);

  useEffect(() => {
    if (!isLoadingRoles) {
      // Reset so a future re-fetch (via the Retry button) gets a fresh window.
      setVerificationTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setVerificationTimedOut(true);
    }, VERIFICATION_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isLoadingRoles]);

  if (isLoading) {
    return <FullPageSpinner label="Initializing..." />;
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // /users/me returned 404: principal is authenticated against the tenant but
  // is not in the allow-list and has no auto-provisioning app role. The backend
  // will 403 every protected endpoint, so block at the UI boundary.
  if (isUnauthorized) {
    return <NotAuthorizedPage variant="not-provisioned" />;
  }

  // /users/me errored with something other than 404 (network, 401, 5xx) OR the
  // request has been pending past our timeout. Either way, we can't confirm
  // the user is allowed in — surface a recovery page with Retry + Sign out
  // instead of falling through to the dashboard, which would 403 every call.
  if (meCheckFailed || verificationTimedOut) {
    return <NotAuthorizedPage variant="verification-failed" />;
  }

  // /users/me still in flight (within the timeout). Wait before mounting the
  // dashboard so its queries don't fire and flash errors during the gap.
  if (isLoadingRoles) {
    return <FullPageSpinner label="Verifying access..." />;
  }

  return <>{children}</>;
}
